import { useState, useEffect, useRef, useCallback } from 'react'
import * as d3 from 'd3'
import { useQuery } from 'react-query'
import { rncpService } from '../../services/rncp'
import LoadingSpinner from '../LoadingSpinner'
import VisualizationControls from './VisualizationControls'
import CompetenceFusion from './CompetenceFusion'
import toast from 'react-hot-toast'

const COLORS = {
  certification: '#3CAF8D', // secondary-500 (Jungle Green)
  block: '#0070DF',         // primary-500 (Celtic Blue)
  competence: '#03256C',    // federal-blue
  fused: '#FFCD38'          // accent-500 (Sunglow)
}

export default function RncpVisualization() {
  // États
  const [filters, setFilters] = useState({
    search: '',
    sectors: [],
    levels: [],
    showCompetences: false,
    maxCompetencesPerBlock: 5 // Limiter à 5 compétences par bloc pour les performances
  })
  const [visibleCertifications, setVisibleCertifications] = useState(new Set())
  const [focusedNode, setFocusedNode] = useState(null)
  const [fusionThreshold, setFusionThreshold] = useState(80)
  const [showFusion, setShowFusion] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(1) // Niveau de zoom actuel

  // Refs
  const svgRef = useRef()
  const containerRef = useRef()
  const simulationRef = useRef()
  const transformRef = useRef(d3.zoomIdentity)

  // Récupération des données
  const { data: visualizationData, isLoading, error, refetch } = useQuery(
    ['rncp-visualization', filters],
    () => rncpService.getVisualizationData({
      sectors: filters.sectors.join(','),
      levels: filters.levels.join(','),
      search: filters.search
    }),
    {
      enabled: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      onSuccess: (data) => {
        // Initialiser toutes les certifications comme visibles
        const certificationIds = new Set(
          data.data.nodes
            .filter(n => n.type === 'certification')
            .map(n => n.id)
        )
        setVisibleCertifications(certificationIds)
      }
    }
  )

  // Analyse de fusion des compétences
  const { data: fusionResult, isLoading: isFusionLoading } = useQuery(
    ['competence-fusion', fusionThreshold, Array.from(visibleCertifications)],
    () => rncpService.getCompetenceFusion({
      threshold: fusionThreshold,
      certificationIds: Array.from(visibleCertifications)
        .map(id => id.replace('cert_', '')) // Retirer le préfixe cert_ pour avoir l'UUID
        .filter(id => id && id !== 'undefined') // Filtrer les IDs invalides
        .join(',')
    }),
    {
      enabled: showFusion && visibleCertifications.size > 0,
      staleTime: 2 * 60 * 1000
    }
  )

  // Fonction de création de la simulation D3.js OPTIMISÉE
  const createSimulation = useCallback((nodes, links) => {
    const width = containerRef.current?.clientWidth || 800
    const height = containerRef.current?.clientHeight || 600

    return d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links)
        .id(d => d.id)
        .strength(1) // Liens très rigides comme dans l'archive
        .distance(link => {
          // Distances adaptées selon le type de lien
          const sourceId = typeof link.source === 'object' ? link.source.id : link.source
          const targetId = typeof link.target === 'object' ? link.target.id : link.target

          if (sourceId && sourceId.toString().startsWith('certification_')) return 80
          if (sourceId && sourceId.toString().startsWith('bloc_')) return 50
          return 30
        })
      )
      .force('charge', d3.forceManyBody().strength(-200)) // Force répulsive constante
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('x', d3.forceX(width / 2).strength(0.05)) // Force de centrage X
      .force('y', d3.forceY(height / 2).strength(0.05)) // Force de centrage Y
      .force('collision', d3.forceCollide().radius(d => {
        if (d.type === 'certification') return 32 // 26 + 6 de l'archive
        if (d.type === 'block') return 24 // 18 + 6
        if (d.type === 'fused') return 26 // Taille dynamique + 6
        return 18 // 12 + 6
      }))
  }, [])

  // Initialiser les certifications visibles quand les données arrivent
  useEffect(() => {
    if (visualizationData?.data?.nodes && visibleCertifications.size === 0) {
      console.log('=== DEBUG FRONTEND ===');
      console.log('Données reçues:', visualizationData.data);
      console.log('Nombre de nœuds:', visualizationData.data.nodes.length);
      console.log('Nombre de liens:', visualizationData.data.links.length);
      console.log('Types de nœuds:', visualizationData.data.nodes.map(n => n.type));

      const certificationNodes = visualizationData.data.nodes
        .filter(n => n.type === 'certification')
        .map(n => n.id)

      console.log('Certifications trouvées:', certificationNodes);

      if (certificationNodes.length > 0) {
        setVisibleCertifications(new Set(certificationNodes))
        console.log('visibleCertifications mis à jour:', certificationNodes);
      }
    }
  }, [visualizationData?.data?.nodes, visibleCertifications.size])

  // Fonction de rendu de la visualisation
  const renderVisualization = useCallback(() => {
    if (!visualizationData?.data || !svgRef.current) return

    console.log('=== DEBUG RENDU ===');
    console.log('visibleCertifications:', Array.from(visibleCertifications));
    console.log('filters.showCompetences:', filters.showCompetences);

    const svg = d3.select(svgRef.current)
    const width = containerRef.current?.clientWidth || 800
    const height = containerRef.current?.clientHeight || 600

    // Nettoyer le SVG
    svg.selectAll('*').remove()

    // Définir les dimensions
    svg.attr('width', width).attr('height', height)

    // Créer le conteneur principal
    const container = svg.append('g').attr('class', 'main-container')

    // Filtrer les données selon la visibilité + LIMITATION DES COMPÉTENCES
    let certNodes = visualizationData.data.nodes.filter(node => {
      if (node.type === 'certification') {
        return visibleCertifications.has(node.id)
      }
      return false
    })

    let blockNodes = visualizationData.data.nodes.filter(node => {
      if (node.type === 'block') {
        return visibleCertifications.has(`cert_${node.certId}`)
      }
      return false
    })

    let competenceNodes = []
    if (filters.showCompetences) {
      // OPTIMISATION : Grouper les compétences par bloc et limiter à maxCompetencesPerBlock
      const competencesByBlock = new Map()

      visualizationData.data.nodes
        .filter(node => node.type === 'competence' && visibleCertifications.has(`cert_${node.certId}`))
        .forEach(node => {
          const blockId = node.blockId || node.parentId
          if (!competencesByBlock.has(blockId)) {
            competencesByBlock.set(blockId, [])
          }
          competencesByBlock.get(blockId).push(node)
        })

      // Ne garder que les N premières compétences par bloc
      competencesByBlock.forEach((comps) => {
        const maxToShow = filters.maxCompetencesPerBlock === 999 ? comps.length : filters.maxCompetencesPerBlock
        const limited = comps
          .sort((a, b) => (a.ordre || 0) - (b.ordre || 0)) // Trier par ordre
          .slice(0, maxToShow)
        competenceNodes.push(...limited)
      })

      const totalCompetences = visualizationData.data.nodes.filter(n => n.type === 'competence').length
      console.log(`Compétences affichées: ${competenceNodes.length} sur ${totalCompetences}`)

      // Avertissement si trop de compétences
      if (competenceNodes.length > 500) {
        toast.warning(`${competenceNodes.length} compétences affichées - l'animation peut être lente`, {
          duration: 3000
        })
      }
    }

    let nodes = [...certNodes, ...blockNodes, ...competenceNodes]
    console.log('Nœuds après filtrage et limitation:', nodes.length);

    let links = visualizationData.data.links.filter(link => {
      const sourceNode = nodes.find(n => n.id === link.source || n.id === link.source.id)
      const targetNode = nodes.find(n => n.id === link.target || n.id === link.target.id)
      return sourceNode && targetNode
    })

    // Appliquer la fusion si activée - MODE SIMPLIFIÉ
    if (showFusion && fusionResult?.data && fusionResult.data.fusedCompetences) {
      console.log('=== FUSION DES COMPÉTENCES - MODE SIMPLIFIÉ ===')
      console.log('Données fusion reçues:', fusionResult.data)

      // Créer uniquement les nœuds fusionnés avec leurs certifications parentes
      const fusedGroups = new Map() // Map<fusedGroupId, fusedNodeData>

      fusionResult.data.fusedCompetences.forEach(fused => {
        if (fused.type === 'fused_competence' && fused.mergedWith) {
          // Pour chaque compétence fusionnée, créer le groupe
          const fusedGroupId = fused.id || `fused_${fused.count}_${Date.now()}`

          fusedGroups.set(fusedGroupId, {
            ...fused,
            id: fusedGroupId,
            type: 'fused',
            certificationIds: new Set(), // On va collecter toutes les certifications parentes
            merged_competences: fused.mergedWith || [], // Pour le tooltip
            x: Math.random() * width,
            y: Math.random() * height
          })
        }
      })

      console.log('Groupes fusionnés créés:', fusedGroups.size)

      // Collecter les certifications pour chaque nœud fusionné
      fusionResult.data.fusedCompetences.forEach(fused => {
        if (fused.type === 'fused_competence' && fused.mergedWith) {
          const fusedGroupId = fused.id
          const fusedGroup = fusedGroups.get(fusedGroupId)

          if (fusedGroup) {
            // Ajouter toutes les certifications qui contiennent ces compétences
            // On parcourt les compétences originales pour trouver leurs certifications
            const mainCompUuid = fused.id.replace('fused_', '')
            const mainComp = competenceNodes.find(c => c.id === `comp_${mainCompUuid}`)

            if (mainComp && mainComp.certId) {
              fusedGroup.certificationIds.add(`cert_${mainComp.certId}`)
            }

            fused.mergedWith.forEach(mergedComp => {
              const comp = competenceNodes.find(c => c.id === `comp_${mergedComp.id}`)
              if (comp && comp.certId) {
                fusedGroup.certificationIds.add(`cert_${comp.certId}`)
              }
            })
          }
        }
      })

      // Créer les nouveaux nœuds fusionnés
      const fusedNodes = Array.from(fusedGroups.values())

      // Filtrer les certifications qui ont au moins un lien vers un nœud fusionné
      const linkedCertIds = new Set()
      fusedNodes.forEach(fusedNode => {
        fusedNode.certificationIds.forEach(certId => linkedCertIds.add(certId))
      })

      const linkedCertNodes = certNodes.filter(cert => linkedCertIds.has(cert.id))

      // MODE SIMPLIFIÉ : uniquement certifications + nœuds fusionnés
      nodes = [...linkedCertNodes, ...fusedNodes]

      console.log('Nœuds après fusion (mode simplifié):', {
        certifications: linkedCertNodes.length,
        noeudsFusionnes: fusedNodes.length,
        total: nodes.length
      })

      // Créer les liens directs : Certification ← → Nœud fusionné
      const newLinks = []

      fusedNodes.forEach(fusedNode => {
        fusedNode.certificationIds.forEach(certId => {
          const certNode = nodes.find(n => n.id === certId)
          if (certNode) {
            newLinks.push({
              source: certId,
              target: fusedNode.id,
              type: 'certification-fused'
            })
          }
        })
      })

      links = newLinks

      console.log('Liens après fusion (mode simplifié):', {
        certificationFused: newLinks.length,
        total: newLinks.length
      })
    }

    // Créer la simulation
    const simulation = createSimulation(nodes, links)
    simulationRef.current = simulation

    // Créer les liens avec optimisation
    const linkElements = container.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('class', 'd3-link')
      .attr('stroke-width', d => {
        if (d.type === 'certification-block') return 3
        return 1.5
      })
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.6)

    // Créer les nœuds avec drag optimisé
    const nodeElements = container.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .call(d3.drag()
        .on('start', (event, d) => {
          // Toujours redémarrer la simulation avec une cible alpha suffisante
          if (!event.active) simulation.alphaTarget(0.3).restart()
          d.fx = d.x
          d.fy = d.y
        })
        .on('drag', (event, d) => {
          d.fx = event.x
          d.fy = event.y
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0)
          // Libérer la position après drag pour permettre la simulation de continuer
          d.fx = null
          d.fy = null
        })
      )

    // Fonction pour créer une forme d'étoile arrondie pour les fusions
    const createStarPath = (radius) => {
      const numPoints = 8
      const wobble = 0.24
      const points = []

      for (let i = 0; i < numPoints; i++) {
        const angle = (i / numPoints) * 2 * Math.PI
        const r = radius * (1 + wobble * Math.sin(angle * numPoints / 2))
        const x = Math.cos(angle) * r
        const y = Math.sin(angle) * r
        points.push({ x, y })
      }

      let path = `M ${points[0].x} ${points[0].y}`
      for (let i = 1; i <= points.length; i++) {
        const p1 = points[(i - 1) % points.length]
        const p2 = points[i % points.length]
        const c1x = p1.x + (p2.x - p1.x) * 0.6
        const c1y = p1.y + (p2.y - p1.y) * 0.6
        path += ` Q ${c1x} ${c1y} ${p2.x} ${p2.y}`
      }
      path += ' Z'
      return path
    }

    // Ajouter les formes pour les nœuds (cercles ou étoiles selon le type)
    nodeElements.each(function(d) {
      const node = d3.select(this)

      if (d.type === 'fused') {
        // Forme d'étoile arrondie pour les compétences fusionnées
        const size = Math.min(15 + (d.count || d.merged_count || 2) * 2, 30)
        const starPath = createStarPath(size)

        node.append('path')
          .attr('d', starPath)
          .attr('fill', COLORS.fused)
          .attr('stroke', '#d97706')
          .attr('stroke-width', 2)
          .style('cursor', 'pointer')
      } else {
        // Cercle normal pour les autres types
        node.append('circle')
          .attr('r', d => {
            if (d.type === 'certification') return 26
            if (d.type === 'block') return 18
            return 12
          })
          .attr('fill', d => COLORS[d.type] || COLORS.competence)
          .attr('stroke', d => {
            if (d.type === 'certification') return '#111827'
            if (d.type === 'block') return '#9ca3af'
            return '#d1d5db'
          })
          .attr('stroke-width', d => {
            if (d.type === 'certification') return 4
            if (d.type === 'block') return 2.5
            return 2
          })
          .style('cursor', 'pointer')
      }
    })

    // Fonction utilitaire pour découper le texte en lignes
    const wrapText = (text, maxCharsPerLine = 30, maxTotalChars = 90) => {
      if (!text) return ['']

      const fullText = String(text)
      let displayText = fullText

      // Tronquer à 90 caractères si nécessaire
      if (fullText.length > maxTotalChars) {
        displayText = fullText.substring(0, maxTotalChars) + '...'
      }

      // Découper en lignes de 30 caractères max sans couper les mots
      const words = displayText.split(' ')
      const lines = []
      let currentLine = ''

      for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word

        if (testLine.length <= maxCharsPerLine) {
          currentLine = testLine
        } else {
          if (currentLine) lines.push(currentLine)
          currentLine = word
        }
      }

      if (currentLine) lines.push(currentLine)

      return lines.length > 0 ? lines : ['']
    }

    // Ajouter les labels avec support multi-lignes
    nodeElements.each(function(d) {
      const nodeGroup = d3.select(this)

      let text = ''
      if (d.type === 'certification') {
        text = d.rncpCode || d.title
      } else if (d.type === 'fused') {
        // Afficher uniquement le nombre pour les compétences fusionnées
        text = String(d.count || d.merged_count || 0)
      } else {
        text = d.title || d.description
      }

      const lines = wrapText(text, 30, 90)
      const fontSize = d.type === 'certification' ? 12 : d.type === 'fused' ? 12 : d.type === 'block' ? 10 : 8
      const lineHeight = fontSize * 1.2
      const startY = d.type === 'certification' ? 45 : 25

      // Créer un groupe pour le texte
      const textGroup = nodeGroup.append('g')
        .attr('class', 'text-group')
        .style('pointer-events', 'none')

      // Ajouter chaque ligne
      lines.forEach((line, i) => {
        textGroup.append('text')
          .text(line)
          .attr('dy', startY + (i * lineHeight))
          .attr('text-anchor', 'middle')
          .style('font-size', `${fontSize}px`)
          .style('font-weight', d.type === 'certification' || d.type === 'fused' ? 'bold' : 'normal')
          .style('fill', d.type === 'fused' ? 'white' : '#374151')
      })
    })

    // Ajouter un titre (tooltip) avec le texte complet au survol
    nodeElements.append('title')
      .text(d => {
        if (d.type === 'certification') return d.title || d.rncpCode || ''
        if (d.type === 'fused') {
          const count = d.count || d.merged_count || 0
          const desc = d.description || d.title || ''
          let tooltip = `Compétences fusionnées (${count})\n${desc}`

          // Ajouter la liste des compétences incluses si disponible
          if (d.merged_competences && d.merged_competences.length > 0) {
            tooltip += '\n\nCompétences incluses:'
            d.merged_competences.forEach(comp => {
              tooltip += `\n• ${comp.description || comp.title || 'N/A'}`
            })
          }

          return tooltip
        }
        return d.title || d.description || ''
      })

    // Gestion des événements de clic
    nodeElements.on('click', (_event, d) => {
      setFocusedNode(d)
      toast.success(`Focus sur: ${d.title || d.description}`)
    })

    // Gestion du zoom optimisée
    let lastZoomLevel = zoomLevel
    const zoom = d3.zoom()
      .scaleExtent([0.1, 8])
      .on('zoom', (event) => {
        transformRef.current = event.transform
        container.attr('transform', event.transform)

        const newZoomLevel = event.transform.k

        // Optimisation: masquer les labels des compétences seulement si on franchit le seuil
        if ((lastZoomLevel >= 1 && newZoomLevel < 1) || (lastZoomLevel < 1 && newZoomLevel >= 1)) {
          if (newZoomLevel < 1) {
            // Masquer les groupes de texte des compétences
            nodeElements.filter(d => d.type === 'competence')
              .selectAll('.text-group')
              .style('opacity', 0)
          } else {
            // Afficher les groupes de texte des compétences
            nodeElements.filter(d => d.type === 'competence')
              .selectAll('.text-group')
              .style('opacity', 1)
          }
          lastZoomLevel = newZoomLevel
        }

        // Mettre à jour le niveau de zoom moins fréquemment (tous les 0.5)
        if (Math.abs(newZoomLevel - zoomLevel) > 0.5) {
          setZoomLevel(newZoomLevel)
        }
      })

    svg.call(zoom)

    // Mise à jour de la simulation avec requestAnimationFrame pour performance optimale
    let animationFrameId = null
    let lastUpdate = Date.now()
    const UPDATE_INTERVAL = 16 // ~60 FPS
    let isRunning = true

    const updatePositions = () => {
      const now = Date.now()

      // Throttle updates pour 60 FPS max
      if (now - lastUpdate >= UPDATE_INTERVAL) {
        linkElements
          .attr('x1', d => d.source.x)
          .attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x)
          .attr('y2', d => d.target.y)

        nodeElements
          .attr('transform', d => `translate(${d.x},${d.y})`)

        lastUpdate = now
      }

      // Continue l'animation en permanence pour permettre le drag
      if (isRunning) {
        animationFrameId = requestAnimationFrame(updatePositions)
      }
    }

    simulation.on('tick', () => {
      // Ne rien faire ici, on gère l'update dans requestAnimationFrame
    })

    // Démarrer l'animation
    animationFrameId = requestAnimationFrame(updatePositions)

    // Arrêter la simulation précédente
    return () => {
      isRunning = false
      simulation.stop()
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  }, [visualizationData, visibleCertifications, filters.showCompetences, filters.maxCompetencesPerBlock, showFusion, fusionResult, createSimulation])

  // Effet pour re-rendre la visualisation
  useEffect(() => {
    if (visualizationData?.data) {
      const cleanup = renderVisualization()
      return cleanup
    }
  }, [renderVisualization])

  // Fonction pour redimensionner
  useEffect(() => {
    const handleResize = () => {
      if (visualizationData?.data) {
        renderVisualization()
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [renderVisualization])

  // Fonctions de contrôle
  const handleZoomIn = () => {
    const svg = d3.select(svgRef.current)
    svg.transition().call(
      d3.zoom().transform,
      transformRef.current.scale(1.5)
    )
  }

  const handleZoomOut = () => {
    const svg = d3.select(svgRef.current)
    svg.transition().call(
      d3.zoom().transform,
      transformRef.current.scale(0.7)
    )
  }

  const toggleAllCertifications = () => {
    if (visibleCertifications.size === 0) {
      // Afficher toutes les certifications
      const allCertifications = new Set(
        visualizationData.data.nodes
          .filter(n => n.type === 'certification')
          .map(n => n.id)
      )
      setVisibleCertifications(allCertifications)
    } else {
      // Masquer toutes les certifications
      setVisibleCertifications(new Set())
    }
  }

  const handleExport = async (format) => {
    try {
      const blob = await rncpService.exportVisualizationData(format, filters)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `rncp-visualization.${format}`
      a.click()
      window.URL.revokeObjectURL(url)
      toast.success(`Export ${format.toUpperCase()} terminé`)
    } catch (error) {
      toast.error('Erreur lors de l\'export')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-lg">Chargement des données RNCP...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Erreur lors du chargement des données</div>
        <button onClick={() => refetch()} className="btn btn-primary">
          Réessayer
        </button>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Contrôles */}
      <VisualizationControls
        filters={filters}
        onFiltersChange={setFilters}
        sectors={visualizationData?.data?.sectors || []}
        statistics={visualizationData?.data?.statistics}
        onToggleAll={toggleAllCertifications}
        showAllVisible={visibleCertifications.size > 0}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onExport={handleExport}
        onDataImported={() => refetch()}
      />

      {/* Fusion des compétences */}
      {filters.showCompetences && (
        <CompetenceFusion
          threshold={fusionThreshold}
          onThresholdChange={setFusionThreshold}
          showFusion={showFusion}
          onToggleFusion={setShowFusion}
          fusionData={fusionResult?.data}
          isLoading={isFusionLoading}
        />
      )}

      {/* Conteneur de visualisation */}
      <div
        ref={containerRef}
        className="flex-1 bg-white rounded-lg border overflow-hidden relative"
        style={{ minHeight: '600px' }}
      >
        <svg
          ref={svgRef}
          className="w-full h-full"
          style={{ cursor: 'grab' }}
        />

        {/* Panneau d'information pour le nœud focalisé */}
        {focusedNode && (
          <div className="absolute top-4 right-4 bg-white p-4 rounded-lg shadow-lg border max-w-md max-h-96 overflow-y-auto">
            {focusedNode.type === 'fused' ? (
              <>
                {/* En-tête du nœud fusionné */}
                <div className="font-bold text-lg text-amber-600 mb-2">
                  {focusedNode.description || focusedNode.title}
                </div>
                <div className="text-sm text-gray-600 mb-3">
                  Fusion de <span className="font-semibold">{focusedNode.count}</span> compétences similaires
                </div>

                {/* Liste des compétences fusionnées */}
                {focusedNode.mergedWith && focusedNode.mergedWith.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-xs font-semibold text-gray-700 uppercase">
                      Compétences incluses :
                    </div>

                    {/* Compétence principale */}
                    <div className="border-l-4 border-amber-400 pl-3 py-2 bg-amber-50">
                      <div className="text-sm font-medium text-gray-900">
                        {focusedNode.description || focusedNode.title}
                      </div>
                      {focusedNode.certifications && focusedNode.certifications[0] && (
                        <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                          <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded font-medium">
                            {focusedNode.certifications[0]}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Compétences fusionnées */}
                    {focusedNode.mergedWith.map((comp, idx) => (
                      <div key={idx} className="border-l-4 border-gray-300 pl-3 py-2 bg-gray-50">
                        <div className="text-sm text-gray-800">
                          {comp.title || 'N/A'}
                        </div>
                        {comp.certificationTitle && (
                          <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                            <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded font-medium">
                              {comp.certificationTitle}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Nœud normal (certification, bloc, compétence) */}
                <div className="font-semibold text-gray-900">
                  {focusedNode.title || focusedNode.description}
                </div>
                {focusedNode.rncpCode && (
                  <div className="text-sm text-gray-600 mt-1">
                    Code RNCP: {focusedNode.rncpCode}
                  </div>
                )}
                {focusedNode.secteur && (
                  <div className="text-sm text-gray-600">
                    Secteur: {focusedNode.secteur}
                  </div>
                )}
              </>
            )}

            <button
              onClick={() => setFocusedNode(null)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100"
            >
              ×
            </button>
          </div>
        )}

        {/* Légende */}
        <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow border">
          <div className="text-sm font-medium text-gray-900 mb-2">Légende</div>
          <div className="space-y-1 text-xs">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS.certification }}></div>
              Certifications
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS.block }}></div>
              Blocs de compétences
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS.competence }}></div>
              Compétences
            </div>
            {showFusion && (
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS.fused }}></div>
                Compétences fusionnées
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}